import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, User, UserX, UserCheck, Key, PlugZap, Trash2, Copy } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function Users({ user }) {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState('');
  const [generatedApiKeyUser, setGeneratedApiKeyUser] = useState(null);
  const [allowlistDialogOpen, setAllowlistDialogOpen] = useState(false);
  const [allowlistUser, setAllowlistUser] = useState(null);
  const [allowlistText, setAllowlistText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const emptyForm = () => ({
    email: '',
    password: '',
    role: 'companyuser',
    company_id: user?.company_id || '',
    allow_external_api: false,
    external_api_allowed_hosts_text: ''
  });
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    loadUsers();
    if (user?.role === 'superadmin') {
      api.get('/companies').then(res => setCompanies(res.data));
    }
  }, [user]);

  const loadUsers = () => {
    api.get('/users').then(res => setUsers(res.data));
  };

  const parseAllowlist = (text) => (
    String(text || '')
      .split(/[\n,]+/)
      .map(item => item.trim())
      .filter(Boolean)
  );

  const formatAllowlist = (values) => (values || []).join('\n');

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        external_api_allowed_hosts: parseAllowlist(formData.external_api_allowed_hosts_text)
      };
      delete payload.external_api_allowed_hosts_text;
      await api.post('/users', payload);
      toast.success('User created');
      setOpen(false);
      setFormData(emptyForm());
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const toggleApiAccess = async (targetUser, currentAccess) => {
    try {
      await api.patch(`/users/${targetUser.id}/api-access`, {
        allow_external_api: !currentAccess,
        external_api_allowed_hosts: currentAccess ? [] : (targetUser.external_api_allowed_hosts || [])
      });
      toast.success(currentAccess ? 'Direct API access disabled' : 'Direct API access enabled');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update API access');
    }
  };

  const openAllowlistDialog = (targetUser) => {
    setAllowlistUser(targetUser);
    setAllowlistText(formatAllowlist(targetUser.external_api_allowed_hosts));
    setAllowlistDialogOpen(true);
  };

  const saveAllowlist = async (e) => {
    e.preventDefault();
    if (!allowlistUser) return;
    try {
      await api.patch(`/users/${allowlistUser.id}/api-access`, {
        allow_external_api: allowlistUser.allow_external_api,
        external_api_allowed_hosts: parseAllowlist(allowlistText)
      });
      toast.success('External API source filter updated');
      setAllowlistDialogOpen(false);
      setAllowlistUser(null);
      setAllowlistText('');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update source filter');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.patch(`/users/${userId}/status`, { is_active: !currentStatus });
      toast.success(currentStatus ? 'User deactivated' : 'User activated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;

    try {
      await api.patch(`/users/${selectedUser.id}/password`, { password: newPassword });
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const confirmDeleteUser = (targetUser) => {
    setDeletingUser(targetUser);
    setDeleteDialogOpen(true);
  };

  const deleteUser = async () => {
    if (!deletingUser) return;
    try {
      await api.delete(`/users/${deletingUser.id}`);
      toast.success('User deleted');
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const generateApiKey = async (targetUser) => {
    try {
      const res = await api.post(`/users/${targetUser.id}/api-key`);
      await navigator.clipboard?.writeText(res.data.api_key);
      setGeneratedApiKey(res.data.api_key);
      setGeneratedApiKeyUser(targetUser);
      setApiKeyDialogOpen(true);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate API key');
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Users</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-button"><Plus className="w-4 h-4 mr-2" />New User</Button>
          </DialogTrigger>
          <DialogContent onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Create User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="user-email-input" />
              </div>
              <div>
                <Label>Password</Label>
                <Input type="password" minLength={10} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="user-password-input" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="companyuser">Company User</SelectItem>
                    <SelectItem value="companyadmin">Company Admin</SelectItem>
                    {user?.role === 'superadmin' && <SelectItem value="superadmin">Super Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {user?.role === 'superadmin' && (
                <div>
                  <Label>Company</Label>
                  <Select value={formData.company_id} onValueChange={(val) => setFormData({...formData, company_id: val})}>
                    <SelectTrigger data-testid="user-company-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>Direct API mail sending</Label>
                  <p className="text-sm text-gray-500">Allow this user to call backend mail methods from external apps.</p>
                </div>
                <Switch
                  checked={formData.allow_external_api}
                  onCheckedChange={(checked) => setFormData({...formData, allow_external_api: checked})}
                  data-testid="user-api-access-switch"
                />
              </div>
              {formData.allow_external_api && (
                <div>
                  <Label>Allowed IPs or Hosts</Label>
                  <Textarea
                    value={formData.external_api_allowed_hosts_text}
                    onChange={(e) => setFormData({...formData, external_api_allowed_hosts_text: e.target.value})}
                    placeholder={'127.0.0.1\n203.0.113.10\n10.0.0.0/24\napi.example.com'}
                    data-testid="user-api-allowlist-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">One per line or comma separated. External API calls are blocked if this list is empty.</p>
                </div>
              )}
              <Button type="submit" className="w-full" data-testid="submit-user-button">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Change Password for {selectedUser?.email}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label>New Password</Label>
              <Input 
                type="password" 
                value={newPassword} 
                onChange={(e) => setNewPassword(e.target.value)} 
                required 
                minLength={10}
                data-testid="new-password-input"
                placeholder="At least 10 chars, upper, lower, number"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="submit-password-button">Change Password</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={apiKeyDialogOpen} onOpenChange={setApiKeyDialogOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>External API Key</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Store this key now for {generatedApiKeyUser?.email}. It will not be shown again.
            </p>
            <div className="rounded-lg border bg-gray-50 p-3">
              <code className="break-all text-sm">{generatedApiKey}</code>
            </div>
            <Button
              type="button"
              className="w-full"
              onClick={async () => {
                await navigator.clipboard?.writeText(generatedApiKey);
                toast.success('API key copied');
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Key
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={allowlistDialogOpen} onOpenChange={setAllowlistDialogOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>External API Source Filter</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveAllowlist} className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-3 text-sm">
              <div className="font-medium">{allowlistUser?.email}</div>
              <div className="text-gray-500">Only requests from these IPs, CIDR ranges, or hosts can use this user's API key.</div>
            </div>
            <div>
              <Label>Allowed IPs or Hosts</Label>
              <Textarea
                value={allowlistText}
                onChange={(e) => setAllowlistText(e.target.value)}
                placeholder={'127.0.0.1\n203.0.113.10\n10.0.0.0/24\napi.example.com'}
                rows={6}
                data-testid="edit-api-allowlist-input"
              />
              <p className="text-xs text-gray-500 mt-1">Use exact IPs, CIDR ranges, or host names. Empty means blocked.</p>
            </div>
            <Button type="submit" className="w-full" data-testid="save-api-allowlist-button">Save Filter</Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Delete {deletingUser?.email}? This removes the user account and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteUser} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="glass-card">
        <div className="p-6">
          <div className="space-y-2">
            {users.map(u => (
              <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`user-item-${u.id}`}>
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-600" />
                  <div>
                    <div className="font-medium">{u.email}</div>
                    <div className="text-sm text-gray-500">{u.role}</div>
                    {u.allow_external_api && (
                      <div className="text-xs text-gray-500">
                        API sources: {(u.external_api_allowed_hosts || []).length ? u.external_api_allowed_hosts.join(', ') : 'Blocked until configured'}
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      Last login: {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'} · Sent: {u.sent_email_count || 0}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${u.allow_external_api ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                    {u.allow_external_api ? (u.has_external_api_key ? 'API key ready' : 'API enabled') : 'Frontend only'}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" data-testid={`user-actions-${u.id}`}>
                        Actions
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        onClick={() => {
                          setSelectedUser(u);
                          setPasswordDialogOpen(true);
                        }}
                        data-testid={`change-password-${u.id}`}
                      >
                        <Key className="w-4 h-4 mr-2" />
                        Change Password
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => toggleApiAccess(u, u.allow_external_api)}
                        data-testid={`toggle-api-access-${u.id}`}
                      >
                        <PlugZap className="w-4 h-4 mr-2" />
                        {u.allow_external_api ? 'Disable API Access' : 'Enable API Access'}
                      </DropdownMenuItem>
                      {u.allow_external_api && (
                        <>
                          <DropdownMenuItem 
                            onClick={() => openAllowlistDialog(u)}
                            data-testid={`edit-api-allowlist-${u.id}`}
                          >
                            <PlugZap className="w-4 h-4 mr-2" />
                            Edit API Source Filter
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => generateApiKey(u)}
                            data-testid={`generate-api-key-${u.id}`}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Generate API Key
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuItem 
                        onClick={() => toggleUserStatus(u.id, u.is_active)}
                        data-testid={`toggle-status-${u.id}`}
                      >
                        {u.is_active ? (
                          <>
                            <UserX className="w-4 h-4 mr-2" />
                            Deactivate User
                          </>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4 mr-2" />
                            Activate User
                          </>
                        )}
                      </DropdownMenuItem>
                      {u.id !== user?.id && (
                        <DropdownMenuItem 
                          onClick={() => confirmDeleteUser(u)}
                          className="text-red-600"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete User
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
